/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useEffect, useCallback } from 'react';

import { useIntl } from 'react-intl';

import { jsx } from '@atlaskit/css';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import { DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

import { createEndAIExperienceCommand } from '../../../pm-plugins/decoration/actions';
import { getPluginState } from '../../../pm-plugins/decoration/decoration-plugin-factory';
import { aiExperienceDecorationPluginKey } from '../../../pm-plugins/decoration/decoration-plugin-key';
import type { AIDecorationExperiencePluginState } from '../../../pm-plugins/decoration/reducer';
import { hasGeneratedContentDecorations } from '../../../pm-plugins/decoration/utils/hasGeneratedContentDecorations';
import type { EndExperience } from '../../../types/types';
import { useAIExperienceService } from '../../hooks/useAIExperienceService';
import { useContentComponentContext } from '../../hooks/useContentComponentContext';
import { getAIHighlightPositions } from '../../utils/selection';
import { AgentFetcher } from '../AgentFetcher/AgentFetcher';
import { LoadableBrowseRovoAgentsModal } from '../BrowseRovoAgentsModal/BrowseRovoAgentsModal';
import { ExperienceApplication } from '../ExperienceApplication/ExperienceApplication';
import { ModalRegionErrorBoundary } from '../ExperienceApplicationErrorBoundary/ExperienceApplicationErrorBoundary';
import { InlineEditReadyBar } from '../InlineEditReadyBar/InlineEditReadyBar';
import { ModalRegion } from '../ModalRegion/ModalRegion';
import { PublishToRovo } from '../PublishToRovo/PublishToRovo';
import { RovoEnabled } from '../RovoEnabled/RovoEnabled';
import { SubscribeToRovo } from '../SubscribeToRovo/SubscribeToRovo';

export const ContentComponent = (): JSX.Element => {
	const {
		aiOptIn,
		editorPluginAIProvider,
		editorView,
		api,
		wrapperElement,
		appearance,
		providerFactory,
		getMentionNameDetails,
		canInlineStreamChanges,
		isRovoLoading,
	} = useContentComponentContext();
	const {
		aiDecorationExperiencePluginState,
		rovoAgentsPluginState,
		editorViewModeMode,
		isInlineEditReady,
	} = useSharedPluginStateWithSelector(api, ['aiExperience', 'editorViewMode'], (states) => ({
		aiDecorationExperiencePluginState: states.aiExperienceState?.aiDecorationExperiencePluginState,
		rovoAgentsPluginState: states.aiExperienceState?.rovoAgentsPluginState,
		editorViewModeMode: states.editorViewModeState?.mode,
		isInlineEditReady:
			states.aiExperienceState?.aiDecorationExperiencePluginState?.isInlineEditReady,
	}));

	const { isRunning } = aiDecorationExperiencePluginState || {};

	const isDecorationSetEmpty =
		!aiDecorationExperiencePluginState ||
		aiDecorationExperiencePluginState.modalDecorationSet === DecorationSet.empty;

	const isViewMode = editorViewModeMode === 'view';
	const decorationPluginState = aiExperienceDecorationPluginKey.getState(editorView.state);
	const intl = useIntl();

	const endExperience: EndExperience = useCallback(
		(options) => {
			// after AI start, there is a chance that the plugin state is not updated yet
			// so we need to check if the plugin state is running before ending the experience
			if (!isRunning) {
				return;
			}

			if (editorPluginAIProvider.onAIProviderChanged) {
				editorPluginAIProvider.onAIProviderChanged('command-palette');
			}

			const state = getPluginState(editorView.state);

			const decoSet =
				hasGeneratedContentDecorations(state) || options?.preserveEditorSelectionOnComplete
					? state.modalDecorationSet
					: undefined;

			const endAIExperienceCommand = createEndAIExperienceCommand(
				decoSet,
				options?.preserveEditorSelectionOnComplete,
				options?.shouldClearSelectionContext,
				options?.shouldIgnoreNextSpaceTrigger,
			);
			endAIExperienceCommand(editorView.state, editorView.dispatch);
		},
		[editorView, isRunning, editorPluginAIProvider],
	);

	useEffect(() => {
		/**
		 * When isDecorationSetEmpty changes from false to true,
		 * we need to end the experience as that ensures all the different AI plugins
		 * are ended.
		 */
		if (
			// eslint-disable-next-line @atlaskit/platform/no-preconditioning
			isDecorationSetEmpty
		) {
			endExperience();
		}
	}, [isDecorationSetEmpty, endExperience]);

	const { send, state, context } = useAIExperienceService({
		context: {
			analyticsContext: { aiSessionId: '', aiGroupId: '' },
			aiProvider: editorPluginAIProvider,
			intl,
			editorView,
			positions: [0, 0],
			isEmpty: true,
			selectionType: 'empty',
			configItem: editorPluginAIProvider?.baseGenerate,
			markdown: '',
			latestPromptResponse: undefined,
			editCount: 0,
			refinementCount: 0,
			retryPromptCount: 0,
			latestPromptRetried: false,
			userInput: '',
			apiErrorInfo: {},
			responseHistory: { positionFromEnd: 0, entries: [], totalSize: 0, traversals: 0 },
			channelId: undefined,
			usePageKnowledge: true,
			endExperience: endExperience,
		},
	});

	/**
	 * We only want to enable directional modal pinning when in the
	 * prompt input state.
	 */
	const isDirectionalPinningEnabled =
		!isRovoLoading &&
		typeof state === 'object' &&
		state['modal active state']['experience'] === 'command palette state';

	// We use this point to render the modal if needed instead of decorators
	// The reason this is preferrable is it has access to mount points
	// where decorators are rendered with a mount point within the Editor content
	// and longer modals get clipped as a result.
	const renderModalRegion = () => {
		// If there are no active decorations -- then we don't want to render the ai experience.
		if (isDecorationSetEmpty) {
			return null;
		}

		if (
			hasGeneratedContentDecorations(
				aiDecorationExperiencePluginState as AIDecorationExperiencePluginState,
			)
		) {
			return null;
		}

		// Get positions from state (calculated by reducer) when feature flag is enabled,
		// otherwise calculate positions inline
		let positions: [number, number] | undefined;
		let decoration;

		if (expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)) {
			positions = aiDecorationExperiencePluginState?.positions;
			if (!positions) {
				return null;
			}

			// Get decoration for modal positioning
			decoration = aiDecorationExperiencePluginState.modalDecorationSet
				.find(
					undefined,
					undefined,
					(spec) =>
						spec.key === 'modalMountPointDecoration' ||
						spec.key === 'modalMountPointRangeDecoration',
				)
				.shift();

			if (!decoration) {
				const nodeDecorations = aiDecorationExperiencePluginState.modalDecorationSet.find(
					undefined,
					undefined,
					(spec) => spec.key === 'ai-nodeDecoration',
				);
				decoration = nodeDecorations.shift();
			}
		} else {
			// Original implementation: calculate positions inline
			// Prioritise getting position from inlineDecoration,
			// otherwise get decoration position from start or end widget
			decoration = aiDecorationExperiencePluginState.modalDecorationSet
				.find(
					undefined,
					undefined,
					(spec) =>
						spec.key === 'modalMountPointDecoration' ||
						spec.key === 'modalMountPointRangeDecoration',
				)
				.shift();

			if (decoration) {
				// when there is decoration, it means it's text selection
				// we need to use the initial positions
				// as the decoration positions are not accurate when there the selection is empty
				if (!aiDecorationExperiencePluginState?.initialPositions) {
					return null;
				}
				positions = aiDecorationExperiencePluginState?.initialPositions;
			} else {
				const nodeDecorations = aiDecorationExperiencePluginState.modalDecorationSet.find(
					undefined,
					undefined,
					(spec) => spec.key === 'ai-nodeDecoration',
				);
				if (nodeDecorations.length > 0) {
					const startPos = Math.min(...nodeDecorations.map((decoration) => decoration.from));
					const endPos = Math.max(...nodeDecorations.map((decoration) => decoration.to));
					positions = [startPos, endPos];
				}
				decoration = nodeDecorations.shift();
			}
		}

		if (!decoration) {
			return null;
		}

		return (
			<ModalRegion
				isDirectionalPinningEnabled={isDirectionalPinningEnabled}
				lastTriggeredFrom={aiDecorationExperiencePluginState.lastTriggeredFrom}
				key={aiDecorationExperiencePluginState.modalMountedTimeStamp}
				decoration={decoration}
				editorView={editorView}
				editorRelativeWrapper={wrapperElement?.offsetParent}
				endExperience={endExperience}
				appearance={appearance}
				decorationSet={aiDecorationExperiencePluginState.modalDecorationSet}
				useFullEditorWidthForUnifiedBar={
					!!aiDecorationExperiencePluginState.inlineRovoStreamingTargetId &&
					fg('use_unified_rovo_thinking_bars_for_remix')
				}
			>
				{aiDecorationExperiencePluginState.configItem && editorView && (
					<ExperienceApplication
						api={api}
						context={context}
						send={send}
						state={state}
						configItem={aiDecorationExperiencePluginState.configItem}
						editorPluginAIProvider={editorPluginAIProvider}
						endExperience={endExperience}
						// Ignored via go/ees005
						// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						positions={positions!}
						editorView={editorView}
						providerFactory={providerFactory}
						lastTriggeredFrom={aiDecorationExperiencePluginState.lastTriggeredFrom}
						triggerMethod={aiDecorationExperiencePluginState.triggerMethod}
						appearance={appearance}
						initialPrompt={aiDecorationExperiencePluginState?.initialPrompt}
						userADFInput={aiDecorationExperiencePluginState?.userADFInput}
						getMentionNameDetails={getMentionNameDetails}
						triggeredFor={aiDecorationExperiencePluginState.triggeredFor}
						engagementPlatformApi={api?.engagementPlatform?.actions}
						initialPositions={aiDecorationExperiencePluginState?.initialPositions}
						nudgeMetrics={aiDecorationExperiencePluginState.nudgeMetrics}
						defaultAgent={rovoAgentsPluginState?.defaultAgent}
						canInlineStreamChanges={canInlineStreamChanges}
						hideLoadingScreen={decorationPluginState?.hideLoadingScreen}
						aiGroupId={aiDecorationExperiencePluginState?.currentSessionId}
						interactionSessionId={aiDecorationExperiencePluginState?.interactionSessionId}
						traceId={aiDecorationExperiencePluginState?.traceId}
						promptVersion={
							fg('platform_editor_ai_promptversion_in_analytics')
								? aiDecorationExperiencePluginState?.promptVersion
								: undefined
						}
					/>
				)}
			</ModalRegion>
		);
	};

	const getPositions = () => {
		return getAIHighlightPositions({
			modalDecorationSet: aiDecorationExperiencePluginState?.modalDecorationSet,
		});
	};

	const renderedSubscribeToRovo = (
		<SubscribeToRovo
			editorApi={api}
			positions={getPositions()}
			editorView={editorView}
			product={editorPluginAIProvider.product}
			onDocChangeByAgent={editorPluginAIProvider.onDocChangeByAgent}
			onAIProviderChanged={editorPluginAIProvider.onAIProviderChanged}
			editorPluginAIProvider={editorPluginAIProvider}
		/>
	);

	return (
		<React.Fragment>
			{fg('platform_editor_ai_remove_agents_list') ? null : (
				<RovoEnabled editorView={editorView} editorPluginAIProvider={editorPluginAIProvider}>
					<PublishToRovo
						editorView={editorView}
						getMentionNameDetails={getMentionNameDetails}
						editorPluginAIProvider={editorPluginAIProvider}
						editorApi={api}
						isViewMode={fg('editor_content_component_pass_view_state') ? isViewMode : undefined}
					/>
					<AgentFetcher editorPluginAIProvider={editorPluginAIProvider} editorView={editorView} />
					{renderedSubscribeToRovo}
				</RovoEnabled>
			)}
			{isInlineEditReady && fg('use_unified_rovo_thinking_bars_for_remix') && (
				<InlineEditReadyBar editorView={editorView} />
			)}
			<ModalRegionErrorBoundary inContentComponent onCloseFallback={endExperience}>
				{renderModalRegion()}
				{rovoAgentsPluginState?.isBrowseModalOpen &&
					!fg('platform_editor_ai_remove_agents_list') && (
						<LoadableBrowseRovoAgentsModal
							editorView={editorView}
							aiGlobalOptIn={aiOptIn}
							editorPluginAIProvider={editorPluginAIProvider}
							startingConfig={aiDecorationExperiencePluginState?.configItem}
							positions={rovoAgentsPluginState?.positions}
						/>
					)}
			</ModalRegionErrorBoundary>
		</React.Fragment>
	);
};
